// 表单
import { useSelector } from 'react-redux';
import V2Title from '@/common/components/Feedback/V2Title';
import { Col, Form, Row, Tooltip, } from 'antd';
import { FC, Fragment, isValidElement, useContext, useEffect, useMemo, useRef, useState } from 'react';
import V2Form from '@/common/components/Form/V2Form';
import V2FormInput from '@/common/components/Form/V2FormInput/V2FormInput';
import V2FormTextArea from '@/common/components/Form/V2FormTextArea/V2FormTextArea';
import V2FormSelect from '@/common/components/Form/V2FormSelect/V2FormSelect';
import { useMethods } from '@lhb/hook';
import V2FormDatePicker from '@/common/components/Form/V2FormDatePicker/V2FormDatePicker';
import V2FormInputNumber from '@/common/components/Form/V2FormInputNumber/V2FormInputNumber';
import V2FormUpload from '@/common/components/Form/V2FormUpload/V2FormUpload';
import IconFont from '@/common/components/IconFont';
import moment from 'moment';
import { editDefaultValue, imagePropertyNames, ShopEditType } from '../ts-config';
import { isArray, isNotEmptyAny } from '@lhb/func';
import FormFranchisee from '@/common/components/business/ExpandStore/TaskCreateDrawer/components/FormFranchisee';
// import { ShopDetailAdditionalItems } from '../../shopDetail/ts-config';
import styles from './index.module.less';
import MapAddressChangeModal from './MapAddressChangeModal';
import { StoreStatus, RentalModel } from '@/common/components/business/ExpandStore/ts-config';
import UserInfoContext from '@/layout/context';
import { globalFranchiseeAlias } from '@/common/utils/ways';
import { getFranchiseeDetail } from '@/common/api/expandStore/franchisee';
import useUpdateEffect from '@/common/hook/updateEffect/useUpdateEffect';

// 需要特殊处理的编辑项
export const UniqueEditCode = ['franchiseeName'];

/**
 * @Description 门店编辑表单
 */
const EditMain: FC<{
  form,
  detail,
  style,
  editType,
  mapPoiInfo,
  setAddressInfo
}> = ({
  form,
  detail,
  style,
  editType,
  mapPoiInfo,
  setAddressInfo,
}) => {
  const [configs, setConfigs] = useState<any>([]);
  const rentModeValue = Form.useWatch('rentMode', form);
  const franchiseeRef: any = useRef();
  const [openMap, setOpenMap] = useState(false); // 打开调整位置弹窗

  const { tenantInfo: { globalAliasItemVOs } } = useContext(UserInfoContext); // 租户信息
  const franchiseeAlias = globalFranchiseeAlias(globalAliasItemVOs);
  const [uniqueName, setUniqueName] = useState<string>(`${franchiseeAlias}唯一标识`); // 加盟商唯一字段名称
  const tenantCheckData = useSelector((state: any) => state.common.tenantCheck);

  const autoPopupedAddressModalRef: any = useRef(false); // 是否已自动弹出过地址选择框

  const isTypeCreate = editType === ShopEditType.Create;
  const isTypeMapCreate = editType === ShopEditType.MapCreate;
  const isTypeEdit = editType === ShopEditType.Edit;
  const isTypeSign = editType === ShopEditType.Sign;

  const showChangeAddressHandle = useMemo(() => {
    if (isTypeMapCreate && isNotEmptyAny(mapPoiInfo)) return false;
    return true;
  }, [isTypeMapCreate, mapPoiInfo]);

  useEffect(() => {
    detail?.uniqueName && setUniqueName(detail.uniqueName);
  }, [detail]);

  useEffect(() => {
    methods.setHiddenField(configs, rentModeValue);
    isNotEmptyAny(configs) && setConfigs([...configs]);
  }, [rentModeValue]);

  useEffect(() => {
    // 创建场景，找到地址选择字段，自动弹出一次地址选择框
    const addressField = configs.find(item => item.code === 'address');
    if ((isTypeCreate) && !autoPopupedAddressModalRef.current && addressField) {
      methods.changeAddressHandle();
      autoPopupedAddressModalRef.current = true;
    }
  }, [configs]);

  /**
    * @description 是否摩可多租户
  */
  const isMKD = useMemo(() => {
    return !!tenantCheckData?.isMkd;
  }, [tenantCheckData]);

  /**
   * @description 是否已勾选门店表头字段-经纬度
   * @return
   */
  const hasCheckedPositionConfig = useMemo(() => {
    return isArray(configs) && !!configs.find((itm: any) => itm.code === 'position' && !!itm.checked);
  }, [configs]);

  useEffect(() => {
    methods.onConfigsChange(detail?.configs);
  }, [detail?.configs]);

  useUpdateEffect(() => {
    // 地图创建场景，找到地址选择字段
    const addressField = configs.find(item => item.code === 'address');
    if (isTypeMapCreate && addressField) {
      if (isNotEmptyAny(mapPoiInfo)) {
        form.setFieldValue('provinceName', mapPoiInfo.provinceName);
        form.setFieldValue('cityName', mapPoiInfo.cityName);
        form.setFieldValue('districtName', mapPoiInfo.districtName);
        form.setFieldValue('address', mapPoiInfo.address);
        form.setFieldValue('position', `${mapPoiInfo.lng},${mapPoiInfo.lat}`);
        form.validateFields(['address', 'position']);
      }
    };
  }, [configs, mapPoiInfo]);

  useEffect(() => {
    if (franchiseeRef.current) {
      const { franchiseeName, uniqueId, franchiseeId } = detail;
      if (franchiseeId) {
        franchiseeRef?.current?.addOption({ name: franchiseeName, id: franchiseeId, uniqueId: uniqueId });
      }
    }
  }, [franchiseeRef?.current]);

  const methods = useMethods({
    onConfigsChange(_configs: any[]) {
      if (!isArray(_configs)) return;

      const fieldsValue = {};
      // 特定字段隐藏，比如创建时隐藏数据来源字段
      const showFields = _configs?.filter(item => item.checked && item.code !== 'contractRemainingDays' && !((isTypeCreate || isTypeMapCreate) && item.field === 'origin'));

      /**
     * 过滤列表不显示的信息，以及合同到期天数
     */
      const result = showFields?.map(item => {

        if (item.field === 'status' && +detail[item.field] === StoreStatus.Opened) { // 如果是门店状态-开业中
          const reulst = showFields.find(itm => itm.field === 'openDate');
          reulst && (reulst.required = true);
        }
        // 地址和经纬度不可编辑
        if (item.field === 'address' || item.field === 'position') {
          item.disabled = true;
        }
        if (item.controlType === 'area') {
          fieldsValue[item.code] = detail[item.code];
        } else if (item.controlType !== 'datepicker') {
          fieldsValue[item.field] = detail[item.field];
        } else {
          fieldsValue[item.field] = detail[item.field] ? moment(detail[item.field]) : undefined;
        }

        if (item.code === 'number') { // 如果是门店编号
          item.rules = item.required ? ([{ required: true, message: `请输入${!item.name || isValidElement(item.name) ? '' : item.name}` }]) : [];
          item.name = <div>
            <Tooltip title={`保证${item.name}唯一`}>
              <span>{item.name}</span>
              <IconFont style={{ color: '#CCCCCD', marginLeft: '3px', marginRight: '5px' }} iconHref='pc-common-icon-ic_info' />
            </Tooltip>
          </div>;
        }

        if (item.code === 'address') { // 如果是地址，需要特殊处理，否则提示红字带英文
          item.rules = item.required ? ([{ required: true, message: `请输入${item.name || ''}` }]) : [];
        }
        if (['contractEnd'].includes(item.code)) {
          item.formItemConfig = {
            rules: [{ validator: methods.validateContractEnd }],
            validateTrigger: ['onChange', 'onBlur', 'onSubmit']
          };
        }
        if (item.code === 'franchiseeName') { // 如果是加盟商
          const { uniqueId } = detail;
          fieldsValue['idCard'] = uniqueId;
        }

        editDefaultValue[item.field] && (item.config = editDefaultValue[item.field]);
        if (item.field === 'attachment') {
          if (isTypeSign) { // 签约场景
            item.config = {
              ...editDefaultValue[item.field],
              maxCount: 10,
              fileType: ['doc', 'docx', 'pdf', 'xls', 'xlsx', 'png', 'jpg', 'jpeg', 'gif', 'bmp']
            };
          }
          // 特定租户特殊处理，固定必填
          isMKD && (item.required = true);
        }
        return item;

      }) || [];

      form?.setFieldsValue(fieldsValue);
      methods.setHiddenField(result, detail.rentMode);
      setConfigs(result);
    },
    /**
     * 设置隐藏字段
     * @param fields 显示fileds
     * @param currentValue 当前选中值，
     */
    setHiddenField(fields, currentValue = '') {
      if (!isNotEmptyAny(fields)) return;
      const firstYearMonthRentField = fields.find(item => item.field === 'firstYearMonthRent');
      const secondYearMonthRentField = fields.find(item => item.field === 'secondYearMonthRent');
      const thirdYearMonthRentField = fields.find(item => item.field === 'thirdYearMonthRent');
      const brokerageField = fields.find(item => item.field === 'brokerage');

      if (+currentValue === RentalModel.Reduce) { // 扣点模式
        firstYearMonthRentField && (firstYearMonthRentField.hidden = true);
        secondYearMonthRentField && (secondYearMonthRentField.hidden = true);
        thirdYearMonthRentField && (thirdYearMonthRentField.hidden = true);
        brokerageField && (brokerageField.hidden = false);
      } else if (+currentValue === RentalModel.High) { // 租金模式-取高模式
        firstYearMonthRentField && (firstYearMonthRentField.hidden = false);
        secondYearMonthRentField && (secondYearMonthRentField.hidden = false);
        thirdYearMonthRentField && (thirdYearMonthRentField.hidden = false);
        brokerageField && (brokerageField.hidden = false);
      } else if (!currentValue) { // 如果没有设置值
        firstYearMonthRentField && (firstYearMonthRentField.hidden = true);
        secondYearMonthRentField && (secondYearMonthRentField.hidden = true);
        thirdYearMonthRentField && (thirdYearMonthRentField.hidden = true);
        brokerageField && (brokerageField.hidden = true);
      } else { // 其他模式
        firstYearMonthRentField && (firstYearMonthRentField.hidden = false);
        secondYearMonthRentField && (secondYearMonthRentField.hidden = false);
        thirdYearMonthRentField && (thirdYearMonthRentField.hidden = false);
        brokerageField && (brokerageField.hidden = true);
      }

      setConfigs(fields);
    },
    /**
     * 获取筛选项的值
     * @param options
     * @returns
     */
    getSelectOptions(options) {
      return options?.map(item => {
        return {
          label: item.name,
          value: item.value,
        };
      });
    },
    handleFranchiseeChange(value) {
      const data = franchiseeRef?.current?.getItem(value);
      form.setFieldsValue({ idCard: data?.uniqueId || '-' });
      data?.uniqueName && (data.uniqueName !== uniqueName) && setUniqueName(data.uniqueName);
    },
    handleFranchiseeCreated(val: any) {
      // 新增加盟商后获取加盟商详情
      methods.getFranchiseeInfo(val);
    },

    getFranchiseeInfo(franId: number) {
      if (franId) {
        getFranchiseeDetail({ id: franId }).then((data) => {
          const { name, uniqueId, uniqueName: uniName } = data;

          const franchiseeField = configs.find(item => item.code === 'franchiseeName');
          const fieldsValue = { idCard: uniqueId };
          franchiseeField && (fieldsValue[franchiseeField.field] = franId);
          form.setFieldsValue(fieldsValue);

          uniName && (uniName !== uniqueName) && setUniqueName(uniName);

          franchiseeRef?.current?.addOption({ name, id: franId, uniqueId });
        });
      } else {
        form.setFieldsValue({ idCard: undefined });
      }
    },

    changeAddressHandle() {
      setOpenMap(true);
    },

    // 自定义校验函数
    validateContractEnd() {
      const start = form.getFieldValue('contractStart');
      const end = form.getFieldValue('contractEnd');
      if (end && start && end.isBefore(start)) {
        return Promise.reject(new Error('结束时间不得早于起始时间'));
      }
      return Promise.resolve();
    }
  });

  return (
    <>
      <V2Form form={form} className={'pt-16 pb-16 pl-40 pr-40'} style={{ ...style }}>
        <V2Title type='H2' text='基本信息' divider className='mb-16'/>
        <Row gutter={16}>
          {configs?.map(item => {
            return (<Fragment key={item.code}>
              {/* 加盟商字段 */}
              {item.code === 'franchiseeName' && !item?.hidden ? <>
                <Col span={8} key={item.code}>
                  <FormFranchisee
                    disabled={item.disabled}
                    label={item.name}
                    name={item.field}
                    required={item.required}
                    rules={item.rules}
                    config={item.config}
                    placeholder={`请输入${item.name}姓名`}
                    onCreated={methods.handleFranchiseeCreated}
                    onChange={methods.handleFranchiseeChange}
                    ref={franchiseeRef}/>
                </Col>
                <Col span={8}>
                  <V2FormInput label={uniqueName} disabled={true} name='idCard'/>
                </Col>
              </> : ''}
              {/* 输入框 */}
              {item.controlType === 'input' && !UniqueEditCode.includes(item.code) && !item?.hidden ? <Col span={8} key={item.code}>
                <V2FormInput
                  disabled={item.disabled}
                  className={styles.formInputContent}
                  label={item.field === 'address' && hasCheckedPositionConfig && showChangeAddressHandle ? (
                    <div className={styles.addressLabel}>
                      <div>{ item.name }</div>
                      <div className={styles.labelRightBtn} onClick={methods.changeAddressHandle}>调整定位</div>
                    </div>
                  ) : item.name}
                  name={item.field}
                  required={item.required}
                  rules={item.rules}
                  config={item.config}
                />
              </Col> : '' }
              {/* 多行输入框 */}
              {item.controlType === 'textarea' && !UniqueEditCode.includes(item.code) && !item?.hidden ? <Col span={8} key={item.code}><V2FormTextArea disabled={item.disabled} label={item.name} name={item.field} required={item.required} config={{ showCount: true, ...item.config }} /> </Col> : ''}
              {/* 下拉选择框 */}
              {item.controlType === 'dropDownRadio' && !UniqueEditCode.includes(item.code) && !item?.hidden ? <Col span={8} key={item.code}><V2FormSelect disabled={item.disabled} label={item.name} name={item.field} required={item.required} options={methods.getSelectOptions(item.selectionList)}/> </Col> : ''}
              {/* 日期选择器 */}
              {item.controlType === 'datepicker' && !UniqueEditCode.includes(item.code) && !item?.hidden ? <Col span={8} key={item.code}><V2FormDatePicker disabled={item.disabled} label={item.name} name={item.field} required={item.required} formItemConfig={item.formItemConfig}/> </Col> : ''}
              {/* 数字输入框 */}
              {item.controlType === 'numberInput' && !UniqueEditCode.includes(item.code) && !item?.hidden ? <Col span={8} key={item.code}><V2FormInputNumber disabled={item.disabled} label={item.name} name={item.field} required={item.required} config={{ ...item?.config, addonAfter: item?.config?.unit, }} /> </Col> : ''}
              {/* 文件上传 */}
              {item.controlType === 'fileUpload' && !UniqueEditCode.includes(item.code) && !item?.hidden ? <Col span={8} key={item.code}><V2FormUpload promptFileAllSuccess label={item.name} name={item.field} required={item.required} config={{ ...item.config, disabled: item.disabled }} uploadType={imagePropertyNames.includes(item.code) ? 'image' : 'file'} /> </Col> : ''}
              {/* 省市区 */}
              {item.controlType === 'area' && !UniqueEditCode.includes(item.code) && !item?.hidden ? <Col span={8} key={item.code}><V2FormInput label={item.name} name={item.code} required={item.required} disabled={true} /> </Col> : ''}
            </Fragment>);
          })}
        </Row>
      </V2Form>
      <MapAddressChangeModal
        open={openMap}
        setOpen={setOpenMap}
        form={form}
        mustSameDistrict={isTypeEdit || isTypeSign}
        setAddressInfo={setAddressInfo}
      />
    </>
  );
};

export default EditMain;
